import { useState, useEffect } from 'react';
import { settingsAPI, cmsAPI } from '@/lib/api';
import api from '@/lib/api';
import { SIDEBAR_MENU_KEYS } from '@/layouts/AdminLayout';
import { resolveImageUrl } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, Key, MessageSquare, RotateCcw, Send, Upload, Image, MessageCircle, Palette, Plug, Bell, Info, Shield } from 'lucide-react';

const DEFAULT_SMS = "Canadian Truck Sales Alert: New unit in stock — [YEAR] [MAKE] [MODEL] • [KM] km • [PRICE]. Photos + details: [LINK]. Call/Text 226-975-9004 for more details";
const DEFAULT_TEMPLATES = {
  new_inventory_email_subject: "New Unit: [YEAR] [MAKE] [MODEL] — Canadian Truck Sales",
  new_inventory_email_body: "Hi,\n\nA new unit is available: [YEAR] [MAKE] [MODEL]\nPrice: [PRICE] | KM: [KM] | Stock: [STOCK]\nLocation: [LOCATION]\n\nView details: [LINK]\n\nCanadian Truck Sales\n(416) 417-2525",
  share_listing_sms: "Canadian Truck Sales: Check out this listing — [YEAR] [MAKE] [MODEL]. [PRICE]. View details at [LINK]. Call/Text 226-975-9004",
  share_listing_email_subject: "[YEAR] [MAKE] [MODEL] — Canadian Truck Sales",
  share_listing_email_body: "Hi,\n\nI wanted to share this listing with you:\n[YEAR] [MAKE] [MODEL]\nPrice: [PRICE] | KM: [KM] | Stock: [STOCK]\nLocation: [LOCATION]\n\nView details: [LINK]\n\nCanadian Truck Sales\n(416) 417-2525",
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [smsTemplate, setSmsTemplate] = useState(DEFAULT_SMS);
  const [templates, setTemplates] = useState({ ...DEFAULT_TEMPLATES });
  const [savingSms, setSavingSms] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testingSms, setTestingSms] = useState(false);
  const [branding, setBranding] = useState({ primary_logo: '', secondary_logo: '' });
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [whatsapp, setWhatsapp] = useState({
    enabled: true,
    phone_number: '14164172525',
    welcome_message: 'Hi! Welcome to Canadian Truck Sales. How can we help you today?',
    reply_time: 'Typically replies within minutes',
    business_name: 'Canadian Truck Sales',
  });
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [menuVisibility, setMenuVisibility] = useState({}); // { admin: {key:bool}, staff: {...} }
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [pageLocks, setPageLocks] = useState({}); // { [key]: { enabled, has_password } }
  const [pwInputs, setPwInputs] = useState({});   // { [key]: { password, saving } }
  const LOCKABLE_PAGES = [
    { key: 'settings', label: 'Settings (this page)' },
    { key: 'system.staff', label: 'Staff' },
    { key: 'system.cms', label: 'CMS' },
    { key: 'system.email_templates', label: 'Email Templates' },
    { key: 'inventory.consignment', label: 'Consignment' },
    { key: 'clients.prospects', label: 'Prospects' },
    { key: 'deals', label: 'Applications' },
    { key: 'financing', label: 'Financing Requests' },
    { key: 'documents', label: 'Documents' },
    { key: 'lenders', label: 'Lenders' },
  ];

  const savePageLock = async (key) => {
    const inp = pwInputs[key] || {};
    setPwInputs(s => ({ ...s, [key]: { ...inp, saving: true } }));
    try {
      const enabled = !!inp.enabled;
      const payload = { enabled };
      if (inp.password) payload.password = inp.password;
      const r = await api.put(`/settings/page-locks/${key}`, payload);
      setPageLocks(p => ({ ...p, [key]: { enabled: r.data.enabled, has_password: r.data.has_password } }));
      setPwInputs(s => ({ ...s, [key]: { enabled, password: '', saving: false } }));
      toast.success(enabled ? 'Page lock saved' : 'Page lock cleared');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save lock';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
      setPwInputs(s => ({ ...s, [key]: { ...inp, saving: false } }));
    }
  };

  const ROLES = [
    { key: 'admin', label: 'Admin' },
    { key: 'staff', label: 'Staff / Salesperson' },
  ];

  const setMenuVal = (role, key, val) => {
    setMenuVisibility(m => ({ ...m, [role]: { ...(m[role] || {}), [key]: val } }));
  };

  const handleSaveMenuVisibility = async () => {
    setSavingVisibility(true);
    try {
      await api.put('/settings/menu-visibility', menuVisibility);
      toast.success('Menu visibility saved. Refresh sidebar to apply.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save menu visibility';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
    }
    setSavingVisibility(false);
  };

  useEffect(() => {
    settingsAPI.get().then(r => setSettings(r.data)).catch(() => {
      setSettings({
        key: 'app_settings',
        twilio: { account_sid: '', auth_token: '', phone_number: '' },
        sendgrid: { api_key: '', from_email: '' },
        docusign: { integration_key: '', secret_key: '', account_id: '' },
        microsoft: { client_id: '', client_secret: '', tenant_id: '' },
      });
    });
    api.get('/settings/menu-visibility').then(r => setMenuVisibility(r.data || {})).catch(() => setMenuVisibility({}));
    api.get('/settings/page-locks').then(r => {
      setPageLocks(r.data || {});
      // Seed input rows with current enabled flag
      const seeded = {};
      Object.entries(r.data || {}).forEach(([k, v]) => { seeded[k] = { enabled: !!v?.enabled, password: '' }; });
      setPwInputs(seeded);
    }).catch(() => setPageLocks({}));
    cmsAPI.get('sms_template').then(r => {
      if (r.data?.template) setSmsTemplate(r.data.template);
    }).catch(() => {});
    cmsAPI.get('message_templates').then(r => {
      if (r.data) setTemplates(t => ({ ...t, ...r.data }));
    }).catch(() => {});
    cmsAPI.get('whatsapp_config').then(r => {
      if (r.data) setWhatsapp(w => ({ ...w, ...r.data }));
    }).catch(() => {});
    settingsAPI.getBranding().then(r => setBranding(r.data || {})).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleSaveTemplates = async () => {
    setSavingSms(true);
    try {
      await cmsAPI.update('sms_template', { template: smsTemplate });
      await cmsAPI.update('message_templates', templates);
      toast.success('All templates saved');
    } catch { toast.error('Save failed'); }
    setSavingSms(false);
  };

  const handleTestSms = async () => {
    if (!testPhone.trim()) { toast.error('Enter a phone number to test'); return; }
    setTestingSms(true);
    try {
      const { data } = await settingsAPI.testSms(testPhone.trim());
      toast.success(data.message || 'Test SMS sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test SMS. Check your Twilio settings.');
    }
    setTestingSms(false);
  };

  const handleLogoUpload = async (type, file) => {
    setUploadingLogo(type);
    try {
      const { data } = await settingsAPI.uploadLogo(type, file);
      setBranding(b => ({ ...b, [`${type}_logo`]: data.url }));
      toast.success(`${type === 'primary' ? 'Primary' : 'Secondary'} logo updated`);
    } catch { toast.error('Failed to upload logo'); }
    setUploadingLogo(null);
  };

  const handleSaveWhatsapp = async () => {
    setSavingWhatsapp(true);
    try {
      await cmsAPI.update('whatsapp_config', whatsapp);
      toast.success('WhatsApp settings saved');
    } catch { toast.error('Failed to save WhatsApp settings'); }
    setSavingWhatsapp(false);
  };

  const updateNested = (section, key, value) => {
    setSettings(s => ({ ...s, [section]: { ...s[section], [key]: value } }));
  };

  if (!settings) return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />;

  const sections = [
    {
      id: 'twilio', title: 'Twilio (SMS)', description: 'Send SMS notifications to clients',
      fields: [
        { key: 'account_sid', label: 'Account SID', placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
        { key: 'auth_token', label: 'Auth Token', placeholder: 'Your auth token', type: 'password' },
        { key: 'phone_number', label: 'Phone Number', placeholder: '+1234567890' },
      ],
    },
    {
      id: 'sendgrid', title: 'SendGrid (Email)', description: 'Send email notifications and quotes',
      fields: [
        { key: 'api_key', label: 'API Key', placeholder: 'SG.xxxxxxxxxxxxxxxx', type: 'password' },
        { key: 'from_email', label: 'From Email', placeholder: 'noreply@canadiantrucksales.ca' },
      ],
    },
    {
      id: 'docusign', title: 'DocuSign (E-Sign)', description: 'Digital signatures for credit applications',
      fields: [
        { key: 'integration_key', label: 'Integration Key', placeholder: 'Your integration key' },
        { key: 'secret_key', label: 'Secret Key', placeholder: 'Your secret key', type: 'password' },
        { key: 'account_id', label: 'Account ID', placeholder: 'Your account ID' },
      ],
    },
    {
      id: 'microsoft', title: 'Microsoft 365 (Email Inbox)', description: 'Outlook email integration via Microsoft Graph',
      fields: [
        { key: 'client_id', label: 'Client ID', placeholder: 'Application (client) ID' },
        { key: 'client_secret', label: 'Client Secret', placeholder: 'Client secret value', type: 'password' },
        { key: 'tenant_id', label: 'Tenant ID', placeholder: 'Directory (tenant) ID' },
      ],
    },
    {
      id: 'square', title: 'Square (Payments)', description: 'Accept hold deposits & payments online via Square',
      fields: [
        { key: 'access_token', label: 'Access Token', placeholder: 'Square sandbox or production access token', type: 'password' },
        { key: 'location_id', label: 'Location ID', placeholder: 'Your Square location ID' },
        { key: 'environment', label: 'Environment', placeholder: 'sandbox or production' },
      ],
    },
  ];

  return (
    <div data-testid="admin-settings">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase">Settings</h1>
          <p className="text-xs text-gray-400 mt-0.5">Configure branding, integrations, and notification templates</p>
        </div>
        <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 mb-6 bg-gray-100" data-testid="settings-tabs">
          <TabsTrigger value="branding" data-testid="tab-branding"><Palette className="w-3.5 h-3.5 mr-1.5" /> Branding</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="w-3.5 h-3.5 mr-1.5" /> Integrations</TabsTrigger>
          <TabsTrigger value="messaging" data-testid="tab-messaging"><Bell className="w-3.5 h-3.5 mr-1.5" /> Messaging</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="w-3.5 h-3.5 mr-1.5" /> Roles &amp; Menu</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about"><Info className="w-3.5 h-3.5 mr-1.5" /> About</TabsTrigger>
        </TabsList>

        {/* === BRANDING TAB === */}
        <TabsContent value="branding" className="space-y-4 mt-0">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Image className="w-4 h-4 text-[#E31937]" /> Branding & Logos
              </CardTitle>
              <p className="text-xs text-gray-400">Upload logos that appear across the website, spec sheets, and PDFs</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[11px] text-gray-500 uppercase block mb-2">Primary Logo (Header, PDFs)</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 flex flex-col items-center gap-3">
                    {branding.primary_logo ? (
                      <img src={resolveImageUrl(branding.primary_logo)} alt="Primary Logo" className="max-h-16 object-contain" data-testid="primary-logo-preview" />
                    ) : (
                      <div className="h-16 flex items-center text-gray-300 text-sm">No logo uploaded</div>
                    )}
                    <Button size="sm" variant="outline" disabled={uploadingLogo === 'primary'} onClick={() => document.getElementById('primary-logo-input').click()} data-testid="upload-primary-logo">
                      <Upload className="w-3 h-3 mr-1" /> {uploadingLogo === 'primary' ? 'Uploading...' : 'Upload Primary Logo'}
                    </Button>
                    <input id="primary-logo-input" type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleLogoUpload('primary', e.target.files[0])} />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 uppercase block mb-2">Secondary Logo (Footer, Small Header)</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 flex flex-col items-center gap-3">
                    {branding.secondary_logo ? (
                      <img src={resolveImageUrl(branding.secondary_logo)} alt="Secondary Logo" className="max-h-16 object-contain" data-testid="secondary-logo-preview" />
                    ) : (
                      <div className="h-16 flex items-center text-gray-300 text-sm">No logo uploaded</div>
                    )}
                    <Button size="sm" variant="outline" disabled={uploadingLogo === 'secondary'} onClick={() => document.getElementById('secondary-logo-input').click()} data-testid="upload-secondary-logo">
                      <Upload className="w-3 h-3 mr-1" /> {uploadingLogo === 'secondary' ? 'Uploading...' : 'Upload Secondary Logo'}
                    </Button>
                    <input id="secondary-logo-input" type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleLogoUpload('secondary', e.target.files[0])} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === INTEGRATIONS TAB === */}
        <TabsContent value="integrations" className="space-y-4 mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sections.map(section => (
              <Card key={section.id} className="bg-white border-gray-200 text-gray-900">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Key className="w-4 h-4 text-[#E31937]" /> {section.title}
                  </CardTitle>
                  <p className="text-xs text-gray-400">{section.description}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {section.fields.map(field => (
                    <div key={field.key}>
                      <label className="text-[11px] text-gray-500 uppercase">{field.label}</label>
                      <Input
                        type={field.type || 'text'}
                        placeholder={field.placeholder}
                        value={settings[section.id]?.[field.key] || ''}
                        onChange={e => updateNested(section.id, field.key, e.target.value)}
                        className="bg-gray-100 border-gray-200 mt-1 text-sm"
                        data-testid={`setting-${section.id}-${field.key}`}
                      />
                    </div>
                  ))}
                  {section.id === 'twilio' && (
                    <div className="pt-2 border-t border-gray-100">
                      <label className="text-[11px] text-gray-500 uppercase">Send Test SMS</label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="tel"
                          placeholder="+1234567890"
                          value={testPhone}
                          onChange={e => setTestPhone(e.target.value)}
                          className="bg-gray-100 border-gray-200 text-sm"
                          data-testid="test-sms-phone"
                        />
                        <Button size="sm" variant="outline" onClick={handleTestSms} disabled={testingSms} data-testid="test-sms-btn"
                          className="border-[#E31937] text-[#E31937] hover:bg-[#E31937] hover:text-white shrink-0">
                          <Send className="w-3 h-3 mr-1" /> {testingSms ? 'Sending...' : 'Test'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* === MESSAGING TAB === */}
        <TabsContent value="messaging" className="space-y-4 mt-0">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Chat Widget
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{whatsapp.enabled ? 'Enabled' : 'Disabled'}</span>
                  <Switch
                    checked={whatsapp.enabled}
                    onCheckedChange={(v) => setWhatsapp(w => ({ ...w, enabled: v }))}
                    data-testid="whatsapp-toggle"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">Configure the floating WhatsApp chat button on your public site</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] text-gray-500 uppercase">WhatsApp Phone Number</label>
                  <Input
                    value={whatsapp.phone_number}
                    onChange={e => setWhatsapp(w => ({ ...w, phone_number: e.target.value }))}
                    placeholder="14164172525"
                    className="bg-gray-100 border-gray-200 mt-1 text-sm"
                    data-testid="whatsapp-phone"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Country code + number, no spaces or dashes (e.g. 14164172525)</p>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 uppercase">Business Name</label>
                  <Input
                    value={whatsapp.business_name}
                    onChange={e => setWhatsapp(w => ({ ...w, business_name: e.target.value }))}
                    placeholder="Canadian Truck Sales"
                    className="bg-gray-100 border-gray-200 mt-1 text-sm"
                    data-testid="whatsapp-business-name"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase">Welcome Message</label>
                <Textarea
                  value={whatsapp.welcome_message}
                  onChange={e => setWhatsapp(w => ({ ...w, welcome_message: e.target.value }))}
                  rows={2}
                  placeholder="Hi! Welcome to Canadian Truck Sales. How can we help you today?"
                  className="bg-gray-100 border-gray-200 mt-1 text-sm"
                  data-testid="whatsapp-welcome-msg"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase">Reply Time Text</label>
                <Input
                  value={whatsapp.reply_time}
                  onChange={e => setWhatsapp(w => ({ ...w, reply_time: e.target.value }))}
                  placeholder="Typically replies within minutes"
                  className="bg-gray-100 border-gray-200 mt-1 text-sm"
                  data-testid="whatsapp-reply-time"
                />
              </div>
              <Button size="sm" className="bg-[#25D366] hover:bg-[#1fb855] text-white" onClick={handleSaveWhatsapp} disabled={savingWhatsapp} data-testid="save-whatsapp-btn">
                <Save className="w-3 h-3 mr-1" /> {savingWhatsapp ? 'Saving...' : 'Save WhatsApp Settings'}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#E31937]" /> Message Templates
              </CardTitle>
              <p className="text-xs text-gray-400">Edit SMS &amp; Email templates used for notifications. Use placeholders: [YEAR] [MAKE] [MODEL] [KM] [PRICE] [STOCK] [LINK] [LOCATION]</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-[11px] text-gray-500 uppercase font-semibold">New Inventory Alert — SMS</label>
                <Textarea
                  value={smsTemplate}
                  onChange={e => setSmsTemplate(e.target.value)}
                  rows={3}
                  className="bg-gray-100 border-gray-200 text-sm font-mono mt-1"
                  data-testid="sms-template-input"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase font-semibold">New Inventory Alert — Email Subject</label>
                <Input
                  value={templates.new_inventory_email_subject || ''}
                  onChange={e => setTemplates(t => ({ ...t, new_inventory_email_subject: e.target.value }))}
                  className="bg-gray-100 border-gray-200 text-sm font-mono mt-1"
                  placeholder="New Unit: [YEAR] [MAKE] [MODEL] — Canadian Truck Sales"
                  data-testid="new-inv-email-subject"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase font-semibold">New Inventory Alert — Email Body</label>
                <Textarea
                  value={templates.new_inventory_email_body || ''}
                  onChange={e => setTemplates(t => ({ ...t, new_inventory_email_body: e.target.value }))}
                  rows={4}
                  className="bg-gray-100 border-gray-200 text-sm font-mono mt-1"
                  placeholder="Hi,&#10;&#10;A new unit is available: [YEAR] [MAKE] [MODEL]&#10;Price: [PRICE] | KM: [KM] | Stock: [STOCK]&#10;&#10;View details: [LINK]"
                  data-testid="new-inv-email-body"
                />
              </div>

              <div className="border-t border-gray-100 pt-3">
                <label className="text-[11px] text-gray-500 uppercase font-semibold">Share Listing — SMS</label>
                <Textarea
                  value={templates.share_listing_sms || ''}
                  onChange={e => setTemplates(t => ({ ...t, share_listing_sms: e.target.value }))}
                  rows={3}
                  className="bg-gray-100 border-gray-200 text-sm font-mono mt-1"
                  placeholder="Canadian Truck Sales: Check out this listing — [YEAR] [MAKE] [MODEL]. View details at [LINK]. Call/Text 226-975-9004"
                  data-testid="share-listing-sms"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase font-semibold">Share Listing — Email Subject</label>
                <Input
                  value={templates.share_listing_email_subject || ''}
                  onChange={e => setTemplates(t => ({ ...t, share_listing_email_subject: e.target.value }))}
                  className="bg-gray-100 border-gray-200 text-sm font-mono mt-1"
                  placeholder="[YEAR] [MAKE] [MODEL] — Canadian Truck Sales"
                  data-testid="share-listing-email-subject"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase font-semibold">Share Listing — Email Body</label>
                <Textarea
                  value={templates.share_listing_email_body || ''}
                  onChange={e => setTemplates(t => ({ ...t, share_listing_email_body: e.target.value }))}
                  rows={4}
                  className="bg-gray-100 border-gray-200 text-sm font-mono mt-1"
                  placeholder="Hi,&#10;&#10;I wanted to share this listing with you: [YEAR] [MAKE] [MODEL]&#10;Price: [PRICE] | KM: [KM] | Stock: [STOCK]&#10;Location: [LOCATION]&#10;&#10;View details: [LINK]"
                  data-testid="share-listing-email-body"
                />
              </div>

              <div className="flex flex-wrap gap-1.5 pt-1">
                {['[YEAR]', '[MAKE]', '[MODEL]', '[KM]', '[PRICE]', '[STOCK]', '[LINK]', '[LOCATION]'].map(tag => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-mono">{tag}</span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSaveTemplates} disabled={savingSms} data-testid="save-templates-btn">
                  <Save className="w-3 h-3 mr-1" /> {savingSms ? 'Saving...' : 'Save All Templates'}
                </Button>
                <Button size="sm" variant="outline" className="border-gray-200 text-gray-500" onClick={() => { setSmsTemplate(DEFAULT_SMS); setTemplates(DEFAULT_TEMPLATES); }} data-testid="reset-templates-btn">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === ROLES & MENU TAB === */}
        <TabsContent value="roles" className="space-y-4 mt-0">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#E31937]" /> Sidebar Menu Visibility
              </CardTitle>
              <p className="text-xs text-gray-400">
                Control which sidebar items are visible per role. Toggle OFF to hide. Admin can never lose access to <strong>Settings</strong>, <strong>System</strong>, or <strong>Staff</strong> (lockout protection).
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="menu-visibility-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-3 text-gray-500 font-semibold uppercase tracking-wide">Menu Item</th>
                      {ROLES.map(r => (
                        <th key={r.key} className="text-center py-2 px-2 text-gray-500 font-semibold uppercase tracking-wide w-24">{r.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {SIDEBAR_MENU_KEYS.map(item => {
                      const isLockedForAdmin = ['settings', 'system', 'system.staff'].includes(item.key);
                      const isSubmenu = item.key.includes('.');
                      return (
                        <tr key={item.key} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className={`py-2 pr-3 ${isSubmenu ? 'pl-6 text-gray-500' : 'font-medium text-gray-700'}`}>
                            {item.label}
                          </td>
                          {ROLES.map(role => {
                            const locked = isLockedForAdmin && role.key === 'admin';
                            const checked = locked ? true : (menuVisibility[role.key]?.[item.key] !== false);
                            return (
                              <td key={role.key} className="text-center py-2 px-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={locked}
                                  onChange={e => setMenuVal(role.key, item.key, e.target.checked)}
                                  className="accent-[#E31937] h-4 w-4 disabled:opacity-50"
                                  data-testid={`menu-vis-${role.key}-${item.key.replace(/\./g, '-')}`}
                                  title={locked ? 'Lockout-protected — always visible to admin' : ''}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSaveMenuVisibility} disabled={savingVisibility} data-testid="save-menu-visibility-btn">
                  <Save className="w-3 h-3 mr-1" /> {savingVisibility ? 'Saving...' : 'Save Menu Visibility'}
                </Button>
                <span className="text-[10px] text-gray-400">Changes apply on next page reload.</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#E31937]" /> Page Password Protection
              </CardTitle>
              <p className="text-xs text-gray-400">
                Add a password lock to any sensitive page. When enabled, anyone accessing the page is prompted for the password and unlocks for 24 hours on that device. Refresh after saving to test.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {LOCKABLE_PAGES.map(p => {
                  const status = pageLocks[p.key] || {};
                  const inp = pwInputs[p.key] || { enabled: !!status.enabled, password: '' };
                  return (
                    <div key={p.key} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto] items-center gap-2 px-3 py-2 border border-gray-100 rounded bg-gray-50">
                      <div>
                        <div className="text-xs font-medium text-gray-700">{p.label}</div>
                        <div className="text-[10px] text-gray-400">
                          {status.enabled
                            ? (status.has_password ? '🔒 Locked' : '⚠ Enabled but no password set')
                            : '🔓 Unlocked'}
                        </div>
                      </div>
                      <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!inp.enabled}
                          onChange={e => setPwInputs(s => ({ ...s, [p.key]: { ...inp, enabled: e.target.checked } }))}
                          className="accent-[#E31937] h-4 w-4"
                          data-testid={`pagelock-toggle-${p.key}`}
                        />
                        Lock
                      </label>
                      <Input
                        type="password"
                        placeholder={status.has_password ? '••••• (leave blank to keep)' : 'Set password (min 4 chars)'}
                        value={inp.password || ''}
                        onChange={e => setPwInputs(s => ({ ...s, [p.key]: { ...inp, password: e.target.value } }))}
                        className="h-8 text-xs bg-white"
                        data-testid={`pagelock-password-${p.key}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => savePageLock(p.key)}
                        disabled={inp.saving}
                        className="h-8 text-xs"
                        data-testid={`pagelock-save-${p.key}`}
                      >
                        {inp.saving ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-400 mt-3">
                <strong>Tip:</strong> The lock applies to anyone reaching the page URL — including admins. If you forget the password, contact support to clear the lock from the database.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === ABOUT TAB === */}
        <TabsContent value="about" className="space-y-4 mt-0">
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-[#E31937]" /> About Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">
                Integration keys are stored securely and used for automated notifications, email, SMS, and e-signature workflows. Add your API keys when ready to enable live integrations. All flows currently work in demo mode.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-500 pt-2 border-t border-gray-100">
                <div><strong className="text-gray-700">Branding:</strong> Logos used across the website, PDFs, spec sheets, and lender packages.</div>
                <div><strong className="text-gray-700">Integrations:</strong> Twilio (SMS), SendGrid (email), DocuSign (e-sign), Microsoft 365 (email inbox), Square (payments).</div>
                <div><strong className="text-gray-700">Messaging:</strong> WhatsApp chat widget &amp; reusable SMS/email templates with placeholders.</div>
                <div><strong className="text-gray-700">Save:</strong> Each section has its own save button; "Save All Settings" persists integration keys.</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
